import { Fragment, useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Wrench, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../core/apiClient';

interface CompanyUser { id: string; firstName: string; lastName: string; }

type MaintenanceStatus = 'OVERDUE' | 'UPCOMING' | 'OK' | 'UNKNOWN';

interface EquipmentMaintenance {
  id: string;
  descripcio: string;
  marca?: string;
  model?: string;
  grup?: string;
  ambit?: string;
  ubicacio?: string;
  plaMant?: string;
  mesosManteniments?: number;
  vencimentPlaManteniment?: string;
  responsableMantenimentUserId?: string;
  proveidorMantenimentUserId?: string;
  maintenanceStatus: MaintenanceStatus;
}

interface MaintenanceLogEntry {
  id: string;
  performedDate: string;
  performedByUserId?: string;
  cost?: number;
  notes?: string;
}

const STATUS_LABEL: Record<MaintenanceStatus, { label: string; cls: string }> = {
  OVERDUE:  { label: 'Vençut', cls: 'bg-red-100 text-red-700' },
  UPCOMING: { label: 'Proper', cls: 'bg-amber-100 text-amber-700' },
  OK:       { label: 'Al dia', cls: 'bg-green-100 text-green-700' },
  UNKNOWN:  { label: 'Sense data', cls: 'bg-gray-100 text-gray-600' },
};

const fmtDate = (v?: string) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ca-ES');
};

export default function Manteniments() {
  const [items, setItems] = useState<EquipmentMaintenance[]>([]);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [logsByEquipment, setLogsByEquipment] = useState<Record<string, MaintenanceLogEntry[]>>({});
  const [registerRowId, setRegisterRowId] = useState<string | null>(null);
  const [performedDate, setPerformedDate] = useState(new Date().toISOString().split('T')[0]);
  const [performedByUserId, setPerformedByUserId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<{ success: boolean; data: EquipmentMaintenance[] }>('/taller/infraestructura/maintenance');
      setItems(res.data ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => {
    api.get<{ success: boolean; data: CompanyUser[] }>('/auth/users').then((r) => setUsers(r.data ?? [])).catch(() => setUsers([]));
  }, []);

  const userName = (id?: string) => {
    if (!id) return '—';
    const u = users.find((x) => x.id === id);
    return u ? `${u.firstName} ${u.lastName}` : '—';
  };

  const toggleExpand = async (id: string) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!logsByEquipment[id]) {
      try {
        const res = await api.get<{ success: boolean; data: MaintenanceLogEntry[] }>(`/taller/infraestructura/${id}/maintenance-log`);
        setLogsByEquipment((p) => ({ ...p, [id]: res.data ?? [] }));
      } catch {
        setLogsByEquipment((p) => ({ ...p, [id]: [] }));
      }
    }
  };

  const openRegister = (id: string) => {
    setRegisterRowId(id);
    setPerformedDate(new Date().toISOString().split('T')[0]);
    setPerformedByUserId('');
    setNotes('');
  };

  const handleRegister = async () => {
    if (!registerRowId) return;
    setSaving(true);
    try {
      await api.post(`/taller/infraestructura/${registerRowId}/maintenance-log`, {
        performedDate,
        performedByUserId: performedByUserId || undefined,
        notes: notes || undefined,
      });
      setRegisterRowId(null);
      setLogsByEquipment((p) => { const n = { ...p }; delete n[registerRowId]; return n; });
      fetchItems();
    } catch (err: any) {
      alert('Error registrant el manteniment: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setSaving(false);
    }
  };

  const overdueCount = items.filter((e) => e.maintenanceStatus === 'OVERDUE').length;
  const upcomingCount = items.filter((e) => e.maintenanceStatus === 'UPCOMING').length;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> {overdueCount} vençuts</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> {upcomingCount} propers (30 dies)</span>
        </div>
        <Button variant="outline" size="sm" onClick={fetchItems}>
          <RefreshCw className="w-4 h-4 mr-1" /> Actualitzar
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500 text-sm">Carregant...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          <Wrench className="w-12 h-12 mx-auto mb-3 opacity-40" />
          Cap equip marcat com a "Necessita manteniment"
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs text-muted-foreground">
            <thead className="bg-slate-100 text-muted-foreground">
              <tr>
                <th className="w-6" />
                <th className="px-3 py-2 text-left font-medium">Equip</th>
                <th className="px-3 py-2 text-left font-medium">Pla de manteniment</th>
                <th className="px-3 py-2 text-left font-medium">Responsable</th>
                <th className="px-3 py-2 text-right font-medium">Venciment</th>
                <th className="px-3 py-2 text-center font-medium">Estat</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <Fragment key={item.id}>
                  <tr className={`border-t ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-2 py-2">
                      <button onClick={() => toggleExpand(item.id)} className="text-gray-400 hover:text-gray-700">
                        {expandedId === item.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {item.descripcio}
                      {(item.marca || item.model) && <span className="text-muted-foreground font-normal ml-1">({[item.marca, item.model].filter(Boolean).join(' ')})</span>}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{item.plaMant || '—'}{item.mesosManteniments ? ` (cada ${item.mesosManteniments} mesos)` : ''}</td>
                    <td className="px-3 py-2">{userName(item.responsableMantenimentUserId || item.proveidorMantenimentUserId)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{fmtDate(item.vencimentPlaManteniment)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_LABEL[item.maintenanceStatus].cls}`}>
                        {STATUS_LABEL[item.maintenanceStatus].label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" className="text-[11px] h-7" onClick={() => openRegister(item.id)}>
                        Registrar manteniment
                      </Button>
                    </td>
                  </tr>
                  {expandedId === item.id && (
                    <tr className="border-t bg-slate-50/60">
                      <td />
                      <td colSpan={6} className="px-3 py-2">
                        <div className="text-[11px] text-muted-foreground font-medium mb-1">Historial de manteniments</div>
                        {!logsByEquipment[item.id] ? (
                          <div className="text-xs text-muted-foreground">Carregant...</div>
                        ) : logsByEquipment[item.id].length === 0 ? (
                          <div className="text-xs text-muted-foreground">Sense manteniments registrats</div>
                        ) : (
                          <table className="w-full text-[11px] text-muted-foreground">
                            <tbody>
                              {logsByEquipment[item.id].map((log) => (
                                <tr key={log.id} className="border-t border-gray-200">
                                  <td className="py-1 pr-3 whitespace-nowrap">{fmtDate(log.performedDate)}</td>
                                  <td className="py-1 pr-3">{userName(log.performedByUserId)}</td>
                                  <td className="py-1 pr-3 text-muted-foreground">{log.notes}</td>
                                  <td className="py-1 text-right whitespace-nowrap">{log.cost != null ? `${log.cost} €` : ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal registrar manteniment */}
      {registerRowId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setRegisterRowId(null)}>
          <div className="bg-white rounded-xl shadow-xl w-[420px]" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-foreground text-sm">Registrar manteniment</h2>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Data realitzat *</label>
                <Input type="date" value={performedDate} onChange={(e) => setPerformedDate(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Realitzat per</label>
                <select className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm" value={performedByUserId} onChange={(e) => setPerformedByUserId(e.target.value)}>
                  <option value="">— Selecciona —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
                <textarea rows={3} className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <p className="text-[11px] text-muted-foreground">El proper venciment es recalcularà automàticament segons la periodicitat de l'equip.</p>
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setRegisterRowId(null)} className="px-3 py-2 text-xs text-muted-foreground hover:bg-gray-100 rounded">
                Cancel·lar
              </button>
              <Button onClick={handleRegister} disabled={saving}>
                {saving ? 'Desant...' : 'Registrar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
